import { useState } from 'react';
import { Box, Modal } from '@mantine/core';
import { Client } from 'src/admin/users/types';
import MemberSearchList from 'src/clients/CreateFamilyV2/MemberSearchList';
import { useFamilyMeetingParticipants } from 'src/meetings/hooks/useFamilyMeetingParticipants';
import type { CalendarEvent } from 'src/meetings/models';
import { showSuccess } from 'src/notifications/notifications';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { SearchPill } from 'src/UI/generate-flow/SearchPill';

type Props = {
  opened: boolean;
  onClose: () => void;
  familyId: string;
  eventId: string;
};

export function ChooseClientModal({ opened, onClose, familyId, eventId }: Props) {
  const { addParticipant, removeParticipant } = useFamilyMeetingParticipants(familyId);
  const [searchTerm, setSearchTerm] = useState('');
  const [addedById, setAddedById] = useState<
    Record<string, { client: Client; participantId: string | null }>
  >({});

  const selectedMembers = Object.values(addedById).map(entry => entry.client);

  async function handleToggle(client: Client) {
    const existing = addedById[client.id];

    if (existing) {
      try {
        if (existing.participantId) {
          await removeParticipant.mutateAsync({ eventId, participantId: existing.participantId });
        }
        setAddedById(previous => {
          const next = { ...previous };
          delete next[client.id];
          return next;
        });
        showSuccess('Removed');
      } catch (error) {
        notifyAndLogError(
          'Failed to remove client',
          'Could not remove the client from this meeting. Please try again.',
          error,
        );
      }
      return;
    }

    try {
      const { data: event } = await addParticipant.mutateAsync({
        eventId,
        body: { participantType: 'CLIENT', clientId: client.id },
      });
      setAddedById(previous => ({
        ...previous,
        [client.id]: { client, participantId: findClientParticipantId(event, client.id) },
      }));
      showSuccess('Added');
    } catch (error) {
      notifyAndLogError(
        'Failed to add client',
        'Could not add the client to this meeting. Please try again.',
        error,
      );
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      size={680}
      padding="lg"
      overlayProps={{ backgroundOpacity: 0.55, blur: 8 }}
      styles={{ content: { backgroundColor: 'transparent', boxShadow: 'none' } }}
    >
      <SearchPill value={searchTerm} onChange={setSearchTerm} placeholder="Search client" />
      <Box mt="sm" bg="white" style={{ borderRadius: 24 }}>
        <MemberSearchList
          selectedMembers={selectedMembers}
          onToggle={handleToggle}
          searchTerm={searchTerm}
          noScroll
          radius={24}
        />
      </Box>
    </Modal>
  );
}

function findClientParticipantId(event: CalendarEvent, clientId: string): string | null {
  const match = event.eventParticipants.find(ep => ep.participant.client?.id === clientId);
  return match?.participant.id ?? null;
}
